import {
  createTheme,
  PaletteOptions,
  responsiveFontSizes,
  BreakpointsOptions,
} from '@mui/material/styles';
import Color from 'color';
import { useThemeMode } from '../../hooks/theme';
import { FontFamilyHandle } from '../../env';

const spacingUnit = 8;

const fontFamily = `"${FontFamilyHandle()}", sans-serif, Arial`;

/**
 * Rewrite MUI's built-in color style
 * Usage: theme.palette.primary.main ...
 */

const fnThemeConfig = (mode: 'light' | 'dark') => {
  const M = (l: string, d?: string) => (mode === 'light' ? l : d || l);

  return {
    palette: {
      mode,
      background: {
        default: M('#FFFFFF', '#040404'),
      },
      primary: {
        main: M('#FF4D67'),
        main_white: M('#FF4D67', '#FFFFFF'),
        main15: M('rgba(105, 76, 230, 0.15)'),
        main50: M('rgba(105, 76, 230, 0.5)', 'rgba(255,255,255,0.5)'),
        light: 'rgb(124, 124, 126)',
        contrastText: '#fff',
        bg_1: M('#FFFFFF','#202020'),
        '3': M('#6D6D6D', '#888888'),
      },
      secondary: {
        main: M('#B0FF92'),
        '500': M('#B8FF9D'),
        '600': M('#B0FF92'),
        '700': M('#9EE683'),
        '950': M('#354C2C'),
      },
      grey: {
        '25': M('#E7E7E7', '#454545'),
        '50': M('#645E7D', '#9591A6'),
        '50_2': M('#645E7D', '#fff'),
        '32': M('#D1D1D1', '#6D6D6D'),
        '34': M('#5D5D5D', '#B0B0B0'),
        '700': M('#29323A', '#EBEAF0'),
        disabled_bg: M('rgba(0,0,0,0.12)', 'rgba(255,255,255,0.12)'),
      },
      neutral: {
        '0_0': M('#fff'),
        '0': M('#fff', '#191528'),
        '25': M('#FCFCFD', '#191528'),
        '25_2': M('#FCFCFD', '#211D35'),
        '50': M('#F9F9FB', '#211D35'),
        '50_2': M('#F9F9FB', '#29253C'),
        '200': M('#FAFAFA', '#3E3956'),
        '200_2': M('#FAFAFA', '#29253C'),
        '300': M('#F7F7F8', '#342F46'),
        '400': M('#F5F5F5', '#342F46'),
        '500': M('#EFEFF1', '#342F46'),
        '600': M('#EBEAF0', '#342F46'),
        '600_1': M('#EBEAF0', '#50496E'),
        '700': M('#DDDBE6', '#000000'),
        '700_1': M('#DDDBE6', '#50496E'),
        '800': M('#A9A6BA', '#29253C'),
        '950': M('#726B8E', '#A9A6BA'),
      },
      danger: {
        '100': M('#FFF2F0'),
        '300': M('#FEB8AE'),
        '400': M('#FD5E49'),
        '500': M('#F15146'),
        '600': M('#F04438'),
        '700': M('#E33D72'),
      },
      base: {
        white: M('#ffffff', '#29253C'),
        white_2: M('#ffffff', '#373348'),
        white_3: M('#ffffff', '#DDDBE6'),
        white_black: M('#ffffff', '#000'),
        whiteonly: '#fff',
        white2white: M('#ffffff', '#ffffff'),
        main2white: M('#5288D3', '#ffffff'),
        lightblue2main: M('#F0EDFD', '#5288D3'),
        white2main: M('#ffffff', '#5288D3'),
        black: M('#000000', '#fff'),
        '2': M('#FEFEFE', '#fff'),
        '3': M('#FEFEFE', '#302C44'),
        '25': M('#F9F9FB', '#342F46'),
        '25_2': M('#F9F9FB', '#29253C'),
        '50': M('#191528', '#DDDBE6'),
        '100': M('#645E7D', '#A9A6BA'),
        '50_50': M('#191528', '#191528'),
        dark2white: M('#191528', '#ffffff'),

      },
      text: {
        disabled: M('#EBEAF0', '#191528'),
      },
      warning: {
        '25': M('#FBFAF8'),
        '50': M('#FFF8EB'),
        '100': M('#FFE4C0'),
        '500': M('#FFAE43'),
        '600': M('#EEA23E'),
        '700': M('#D78100'),
      },
      success: {
        main: M('#41AE49'),
      },
      info: {
        main: M('#5288D3', '#fff'),
      },
      action: {
        active: M('#212121', '#FFFFFF'),
        hover: M('#F9F8FE'),
        hoverOpacity: 0.04,
        selected: M('rgba(0, 0, 0, 0.08)'),
        selectedOpacity: 0.08,
        disabled: M('rgba(0, 0, 0, 0.26)'),
        disabledBackground: M('rgba(6,3,21,0.5)'),
        disabledOpacity: 0.38,
        focus: M('rgba(0, 0, 0, 0.12)'),
        focusOpacity: 0.12,
        activatedOpacity: 0.12,
        bgPrimary: M('rgba(248,182,255,0.2)'),
      },
      gradient: {
        overlayColor: M('rgba(105, 76, 230, 0.8)'),
      },
    },
    breakpoints: {
      keys: ['xs', 'sm', 'md', 'lg', 'xl'],
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1080,
        xl: 1440,
        homepage: 1440,
      },
      unit: 'px',
    },
    typography: {
      fontFamily: fontFamily,
      htmlFontSize: 100,
    },
    spacing: spacingUnit,
    shape: {
      borderRadius: 0,
    },
    zIndex: {
      modal: 1000,
    },
    shadow: {
      sxm: '0px 1px 2px ' + M(`rgba(25, 21, 40, 0.04)`),
    },
    custom: {
      txt: M('#212121', '#FFFFFF'),
      txt1: "#9E9E9E",
      txt2: "#E0E0E0",
      primary5: M('#F9F8FE', '#3E3956'),
      btn_disb: "#DF485E",
      btn_hover: "#FF667D",
      light_hover: "#FF4D673C",
      bg_opt1: M('#FFFFFF', '#181A20D9'),
      bg_opt2: M('#2A1E25', '#2A1E25'),
      bg_opt3: "#FF4D6714",
      bg_opt4: "#FF4D676A",
      col1: M('#EEEEEE', '#35383F'),
      col2: M('#EEEEEE', '#45484D'),
      bg1: M('#FFFFFF', '#1F222A'),
      while: "#FFFFFF",
      bg_while_btn: "#FFFFFF3C",
      bg_while_btn1: "#FFFFFF0F",
      bg_while_btn_h: "#FFFFFF4C",
      bg_black_btn: "#0000003C",
    },
  };
};

const themeConfigForModule = fnThemeConfig('light');
declare module '@mui/material/styles' {
  export interface Theme {
    palette: typeof themeConfigForModule.palette;
    shadow: typeof themeConfigForModule.shadow;
    custom: typeof themeConfigForModule.custom;
  }
  interface ThemeOptions {
    palett?: typeof themeConfigForModule.palette;
    shadow?: typeof themeConfigForModule.shadow;
    custom?: typeof themeConfigForModule.custom;
  }
}

function useTheme() {
  const themeMode = useThemeMode();
  // const themeMode = "dark"
  const themeConfig = fnThemeConfig(themeMode);

  let theme = createTheme({
    palette: themeConfig.palette as PaletteOptions,
    breakpoints: themeConfig.breakpoints as BreakpointsOptions,
    typography: themeConfig.typography,
    spacing: themeConfig.spacing,
    shape: themeConfig.shape,
    shadow: themeConfig.shadow,
    custom: themeConfig.custom,
    components: {
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root:hover': {
              backgroundColor: `${themeConfig.palette.warning[25]} !important`,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: '0.14rem',
            color: themeConfig.custom.txt,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: '0.13rem',
            color: themeConfig.palette.base.main2white,
            backgroundColor: themeConfig.palette.base.lightblue2main,
            borderRadius: '0.04rem',
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            padding: '0 !important',
          },
        },
      },
      // MuiMenu: {
      //   styleOverrides: { root: { '&>.MuiPaper-root': {}, }, },
      // },
      MuiList: {
        styleOverrides: {
          root: {
            '&>li': {
              borderLeft: `0.01rem solid ${themeConfig.palette.neutral['700_1']}`,
              borderRight: `0.01rem solid ${themeConfig.palette.neutral['700_1']}`,
            },
            '&>li:last-of-type': {
              borderBottom: `0.01rem solid ${themeConfig.palette.neutral['700_1']}`,
              borderBottomLeftRadius: '0.1rem',
              borderBottomRightRadius: '0.1rem',
              borderTop: '0 !important',
            },
            '&>li:first-of-type': {
              borderTop: `0.01rem solid ${themeConfig.palette.neutral['700_1']}`,
              borderTopLeftRadius: '0.1rem',
              borderTopRightRadius: '0.1rem',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: '10px',
            fontWeight: 400,
            fontSize: '0.14rem',
            color: themeConfig.palette.grey[700],
            height: '0.4rem',
            backgroundColor: themeConfig.palette.base.white,
            '&.Mui-selected': {
              backgroundColor: `${themeConfig.custom.primary5} !important`,
            },
            ':hover': {
              color: `${themeConfig.palette.grey[700]} !important`,
              backgroundColor: `${themeConfig.custom.primary5} !important`,
            },
            '&.Mui-focusVisible': {
              backgroundColor: `${themeConfig.custom.primary5} !important`,
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          popper: {
            boxShadow: `0px 1px 2px rgba(25, 21, 40, 0.04)`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: `4px`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              opacity: '1',
              filter: 'grayscale(30%)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: '0.14rem',
            boxShadow: 'none',
            textTransform: 'capitalize',
            '&:hover': {
              backgroundColor: Color(themeConfig.palette.primary.main)
                .fade(0.1)
                .toString(),
            },
            '&.Mui-disabled': {
              backgroundColor: themeConfig.custom.btn_disb,
              opacity: '1',
              filter: 'grayscale(30%)',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontWeight: 400,
            fontSize: '15px',
            color: themeConfig.palette.grey['50_2'],
          },
        },
      },
    },
  });

  theme = responsiveFontSizes(theme);
  return theme;
}

export { useTheme };
